import type { DefaultColumn, SpecificColumn } from '../schemas';
import type { CssResolvers, CssVars } from '../../css-vars';

import { DEVICES } from '../../utils';
import { animationOpacityVar } from '../../features/animation/animation.css-vars';
import { backgroundVars } from '../../features/background/background.css-vars';
import { borderRadiusVars } from '../../features/border-radius/border-radius.css-vars';
import { borderVars } from '../../features/border/border.css-vars';
import { shadowVar } from '../../features/shadow/shadow.css-vars';

import { flexAlignVar, responsiveSpacingVars, textAlignVar } from '../../mixins';

/**
 * CSS custom properties for a column — the replacement for the former JSS
 * `getStyles`. The static rules live in `./column.base.less`; only the
 * per-column values travel through here, so the editor and the published page
 * compute the same bag.
 *
 * **Colour and image resolution are injected**, for the same reason as the
 * section builder: the package is framework-free, and the image resolver also
 * carries the app's own CDN base.
 */
export type ColumnDefaultProps = DefaultColumn['props'];
export type ColumnBackgroundProps = ColumnDefaultProps['background'];
export type ColumnBackgroundImage = ColumnBackgroundProps['image']['desktop'];

/**
 * One function per variable, so a host reading its fields from a signal form can
 * bind them one by one and only rewrite the property that actually changed. The
 * grouped builders below compose these, which keeps the two shapes from drifting.
 */

/** Gap the row puts between its columns; halved onto each side. */
export function columnGapHalfVar(columnGap: unknown): string {
    return `${Number(columnGap ?? 0) / 2}px`;
}

export type ColumnCssVarsParams = {
    column: DefaultColumn;
    /** Gap the row puts between its columns; halved onto each side. */
    columnGap?: number;
    /** Preview keeps animated columns visible rather than waiting for GSAP. */
    isPreview?: boolean;
};

/**
 * Every variable a default column needs, for all three breakpoints. Unlike the
 * legacy builder this emits the full set unconditionally: v0 resolved the
 * "device inherits desktop" cascade at migration time, so there is nothing left
 * to leave unset.
 */
export function getColumnCssVars(
    { column, columnGap = 0, isPreview = false }: ColumnCssVarsParams,
    resolvers: CssResolvers
): CssVars {
    const { props } = column;

    const vars: CssVars = {
        '--column-gap-half': columnGapHalfVar(columnGap),
        '--column-justify': flexAlignVar(props.verticalAlign),
    };

    DEVICES.forEach((device) => {
        Object.assign(
            vars,
            borderRadiusVars(props.borderRadius, 'column', device),
            responsiveSpacingVars(props.padding, 'column-padding', device),
            borderVars(props.border, 'column', device),
            backgroundVars(props.background, 'column', resolvers, device)
        );
    });

    const shadow = shadowVar(props.shadow);
    if (shadow) {
        vars['--column-shadow'] = shadow;
    }

    const opacity = animationOpacityVar(props.animation, isPreview);
    if (opacity) {
        vars['--column-opacity'] = opacity;
    }

    return vars;
}

export type SpecificColumnCssVarsParams = {
    column: SpecificColumn;
    /** Vertical position of the header row, in the section's own v0 vocabulary. */
    sectionVerticalPosition?: string;
    /** Horizontal position the block falls back to once the row wraps. */
    blockHorizontalPosition?: string;
};

/** Alignment variables for a header (`specific`) column, as one bag. */
export function getSpecificColumnCssVars({
    column,
    sectionVerticalPosition,
    blockHorizontalPosition,
}: SpecificColumnCssVarsParams): CssVars {
    const horizontalAlign = column.props?.horizontalAlign;

    return {
        '--column-justify': flexAlignVar(horizontalAlign),
        '--column-align': flexAlignVar(sectionVerticalPosition),
        '--column-text-align': textAlignVar(horizontalAlign),
        '--column-narrow-align': flexAlignVar(blockHorizontalPosition),
        '--column-narrow-text-align': textAlignVar(blockHorizontalPosition),
    };
}
